export interface ApiMeta {
  requestId?: string;
  [key: string]: unknown;
}

export interface ApiEnvelope<T> {
  data: T;
  meta?: ApiMeta;
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

interface ApiFailure {
  error?: { code?: string; message?: string };
  meta?: { requestId?: string };
}

export class ApiError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly status: number;

  constructor(input: {
    code: string;
    message: string;
    requestId?: string;
    status: number;
  }) {
    super(input.message);
    this.name = "ApiError";
    this.code = input.code;
    this.requestId = input.requestId;
    this.status = input.status;
  }
}

export type ApiRequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  rawBody?: BodyInit;
};

function isApiFailure(value: unknown): value is ApiFailure {
  return value !== null && typeof value === "object";
}

export async function apiEnvelope<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const headers = new Headers(options.headers);
  headers.set("accept", "application/json");
  const hasJsonBody = options.body !== undefined;
  if (hasJsonBody) headers.set("content-type", "application/json");

  const response = await fetch(path, {
    ...options,
    body: options.rawBody ?? (hasJsonBody ? JSON.stringify(options.body) : undefined),
    credentials: "same-origin",
    headers,
  });
  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const failure = isApiFailure(payload) ? payload : {};
    throw new ApiError({
      code: failure.error?.code ?? "REQUEST_FAILED",
      message:
        failure.error?.message ??
        (typeof payload === "string" && payload
          ? payload
          : "The request could not be completed"),
      requestId:
        failure.meta?.requestId ?? response.headers.get("x-request-id") ?? undefined,
      status: response.status,
    });
  }
  if (!isApiFailure(payload) || !("data" in payload)) {
    throw new ApiError({
      code: "INVALID_RESPONSE",
      message: "The server returned an invalid response",
      requestId: response.headers.get("x-request-id") ?? undefined,
      status: response.status,
    });
  }
  return payload as ApiEnvelope<T>;
}

export async function apiRequest<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  return (await apiEnvelope<T>(path, options)).data;
}

export function readableApiError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}
